import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { api, formatGBP, CATEGORIES } from "@/lib/api";
import { Plus, Edit, Trash2, X, Package, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const emptyProduct = {
  name: "",
  category: "wigs",
  description: "",
  price: 0,
  image_url: "",
  images: [],
  length: "",
  texture: "",
  color: "",
  stock: 0,
  featured: false,
  lengths: [],
  textures: [],
  colors: [],
};

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [modal, setModal] = useState(null); // null | product or 'new'
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login", { state: { from: "/admin" } });
  }, [loading, user, navigate]);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));

  useEffect(() => {
    if (user?.role === "admin") {
      loadProducts();
      loadOrders();
    }
  }, [user]);

  if (loading || !user || user.role !== "admin") return <div className="py-32 text-center text-[#5C4A4D]">Loading...</div>;

  const openNew = () => { setForm(emptyProduct); setModal("new"); };
  const openEdit = (p) => {
    setForm({
      ...p,
      images: p.images || [],
      lengths: p.lengths || [],
      textures: p.textures || [],
      colors: p.colors || [],
    });
    setModal(p);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        stock: parseInt(form.stock) || 0,
        images: typeof form.images === "string" ? form.images.split("\n").map(s => s.trim()).filter(Boolean) : form.images,
        lengths: typeof form.lengths === "string" ? form.lengths.split(",").map(s => s.trim()).filter(Boolean) : form.lengths,
        textures: typeof form.textures === "string" ? form.textures.split(",").map(s => s.trim()).filter(Boolean) : form.textures,
        colors: typeof form.colors === "string" ? form.colors.split(",").map(s => s.trim()).filter(Boolean) : form.colors,
      };
      if (modal === "new") {
        await api.post("/products", payload);
        toast.success("Product added");
      } else {
        await api.put(`/products/${modal.product_id}`, payload);
        toast.success("Product updated");
      }
      setModal(null);
      loadProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.product_id}`);
      toast.success("Deleted");
      loadProducts();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const updateStock = async (p, stock) => {
    try {
      await api.patch(`/products/${p.product_id}/stock`, null, { params: { stock } });
      loadProducts();
    } catch {
      toast.error("Failed to update stock");
    }
  };

  return (
    <div className="bg-[#FFF4E0] min-h-screen" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="overline mb-2">House Console</div>
            <h1 className="font-serif-display text-5xl text-[#1A0F11]">Admin</h1>
          </div>
          {tab === "products" && (
            <button onClick={openNew} className="btn-primary" data-testid="admin-add-product"><Plus size={16}/> Add Product</button>
          )}
        </div>

        <div className="flex gap-2 border-b border-[#E8D8C8] mb-8">
          <button onClick={() => setTab("products")} className={`px-4 py-3 text-xs uppercase tracking-[0.2em] flex items-center gap-2 border-b-2 ${tab === "products" ? "border-[#831132] text-[#831132]" : "border-transparent text-[#5C4A4D]"}`} data-testid="admin-tab-products"><Package size={14}/> Products ({products.length})</button>
          <button onClick={() => setTab("orders")} className={`px-4 py-3 text-xs uppercase tracking-[0.2em] flex items-center gap-2 border-b-2 ${tab === "orders" ? "border-[#831132] text-[#831132]" : "border-transparent text-[#5C4A4D]"}`} data-testid="admin-tab-orders"><ShoppingBag size={14}/> Orders ({orders.length})</button>
        </div>

        {tab === "products" && (
          <div className="overflow-x-auto bg-[#FDF1EC]">
            <table className="w-full text-sm" data-testid="admin-product-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#5C4A4D] border-b border-[#E8D8C8]">
                  <th className="py-4 px-4">Product</th>
                  <th className="py-4 px-4">Category</th>
                  <th className="py-4 px-4">Price</th>
                  <th className="py-4 px-4">Stock</th>
                  <th className="py-4 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.product_id} className="border-b border-[#E8D8C8]/60" data-testid={`admin-product-${p.product_id}`}>
                    <td className="py-3 px-4 flex items-center gap-3">
                      <img src={p.image_url} alt="" className="w-12 h-14 object-cover" />
                      <div>
                        <div className="text-[#1A0F11] font-medium">{p.name}</div>
                        <div className="text-xs text-[#5C4A4D]">{p.slug}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4 capitalize">{p.category.replace("-", " ")}</td>
                    <td className="py-3 px-4">{formatGBP(p.price)}</td>
                    <td className="py-3 px-4">
                      <input
                        type="number"
                        defaultValue={p.stock}
                        onBlur={(e) => updateStock(p, parseInt(e.target.value) || 0)}
                        className="w-20 bg-transparent border border-[#E8D8C8] px-2 py-1 text-sm"
                        data-testid={`admin-stock-${p.product_id}`}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(p)} className="p-2 hover:bg-[#FFF4E0]" data-testid={`admin-edit-${p.product_id}`} aria-label="Edit"><Edit size={14}/></button>
                        <button onClick={() => remove(p)} className="p-2 hover:bg-[#FFF4E0] text-[#831132]" data-testid={`admin-delete-${p.product_id}`} aria-label="Delete"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3">
            {orders.length === 0 && <p className="text-[#5C4A4D]">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.order_id} className="bg-[#FDF1EC] p-6 flex flex-wrap items-start justify-between gap-4" data-testid={`admin-order-${o.order_id}`}>
                <div>
                  <div className="overline">{o.order_id}</div>
                  <div className="font-serif-display text-xl text-[#1A0F11] mt-1">{o.shipping?.full_name} · {o.email}</div>
                  <div className="text-xs text-[#5C4A4D] mt-1">{new Date(o.created_at).toLocaleString("en-GB")}</div>
                  <div className="text-xs text-[#5C4A4D] mt-1">{o.items.length} items · {o.shipping?.city}, {o.shipping?.country}</div>
                </div>
                <div className="text-right">
                  <div className={`text-xs uppercase tracking-[0.18em] ${o.status === "confirmed" ? "text-[#2D4A22]" : "text-[#831132]"}`}>{o.status}</div>
                  <div className="font-serif-display text-2xl text-[#1A0F11] mt-1">{formatGBP(o.total)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-4 bg-[#1A0F11]/40 backdrop-blur-sm overflow-y-auto" data-testid="admin-product-modal">
          <form onSubmit={submitForm} className="bg-[#FFF4E0] w-full max-w-3xl my-8 p-8 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-serif-display text-3xl text-[#1A0F11]">{modal === "new" ? "Add Product" : "Edit Product"}</h2>
              <button type="button" onClick={() => setModal(null)} data-testid="admin-modal-close" aria-label="Close"><X size={22}/></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2"><div className="label-luxe">Name</div><input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-luxe" data-testid="admin-form-name"/></div>
              <div>
                <div className="label-luxe">Category</div>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="input-luxe" data-testid="admin-form-category">
                  {CATEGORIES.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
              </div>
              <div><div className="label-luxe">Price (GBP)</div><input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} className="input-luxe" data-testid="admin-form-price"/></div>
              <div className="md:col-span-2"><div className="label-luxe">Description</div><textarea required rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-luxe resize-none" data-testid="admin-form-description"/></div>
              <div className="md:col-span-2"><div className="label-luxe">Main image URL</div><input required value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} className="input-luxe" data-testid="admin-form-image"/></div>
              <div className="md:col-span-2"><div className="label-luxe">Additional images (one URL per line)</div><textarea rows={2} value={Array.isArray(form.images) ? form.images.join("\n") : form.images} onChange={(e) => setForm({...form, images: e.target.value})} className="input-luxe resize-none" data-testid="admin-form-images"/></div>
              <div><div className="label-luxe">Default length</div><input value={form.length || ""} onChange={(e) => setForm({...form, length: e.target.value})} className="input-luxe" placeholder="20 inches"/></div>
              <div><div className="label-luxe">Default texture</div><input value={form.texture || ""} onChange={(e) => setForm({...form, texture: e.target.value})} className="input-luxe" placeholder="Body Wave"/></div>
              <div><div className="label-luxe">Default colour</div><input value={form.color || ""} onChange={(e) => setForm({...form, color: e.target.value})} className="input-luxe" placeholder="Natural Black"/></div>
              <div><div className="label-luxe">Stock</div><input required type="number" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})} className="input-luxe" data-testid="admin-form-stock"/></div>
              <div><div className="label-luxe">Length options (comma separated)</div><input value={Array.isArray(form.lengths) ? form.lengths.join(", ") : form.lengths} onChange={(e) => setForm({...form, lengths: e.target.value})} className="input-luxe"/></div>
              <div><div className="label-luxe">Texture options (comma separated)</div><input value={Array.isArray(form.textures) ? form.textures.join(", ") : form.textures} onChange={(e) => setForm({...form, textures: e.target.value})} className="input-luxe"/></div>
              <div><div className="label-luxe">Colour options (comma separated)</div><input value={Array.isArray(form.colors) ? form.colors.join(", ") : form.colors} onChange={(e) => setForm({...form, colors: e.target.value})} className="input-luxe"/></div>
              <div className="md:col-span-2 flex items-center gap-2">
                <input id="featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} data-testid="admin-form-featured"/>
                <label htmlFor="featured" className="text-sm text-[#1A0F11]">Featured on homepage</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-[#E8D8C8]">
              <button type="button" onClick={() => setModal(null)} className="btn-outline">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="admin-form-save">{saving ? "Saving..." : "Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
